"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ListFilter,
  X,
} from "lucide-react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  formatMarketplaceDateRange,
  hasCompleteDateRange,
} from "./marketplaceDateUtils";
import { MarketplaceMobileDatePickerPanel } from "./MarketplaceMobileDatePickerPanel";
import { useIsMobileViewport } from "./useIsMobileViewport";
import { cn } from "@/lib/utils";
import { getCheckboxSelection } from "./appliedFilterUtils";
import { DateRangeFilter } from "./DateRangeFilter";
import { FilterDropdown } from "./FilterDropdown";
import { FilterBarTagsSkeleton } from "./FilterBarSkeleton";
import { filterChipActiveClass, filterChipInactiveClass } from "./filterStyles";
import { MARKETPLACE_KEYWORDS_FILTER_TYPE } from "@/lib/marketplace";
import type {
  MarketplaceDateFilterValue,
  MarketplaceDynamicFilter,
  MarketplaceFilterValue,
  MarketplaceSelectedFilters,
} from "@/types/api/marketplace";

const MAX_KEYWORD_CHIPS = 10;

export interface FilterBarProps {
  filters: MarketplaceDynamicFilter[];
  selectedFilters: MarketplaceSelectedFilters;
  onFilterChange: (
    type: string,
    value: MarketplaceFilterValue | undefined
  ) => void;
  onOpenFiltersModal: () => void;
  activeModalFilterCount: number;
  isFetchingFilters?: boolean;
}

type FilterChipItem = {
  key: string;
  filterType: string;
  optionId: number;
  label: string;
  isSelected: boolean;
  sortIndex: number;
};

type BarTagItem =
  | {
      kind: "dropdown";
      key: string;
      isSelected: boolean;
      sortIndex: number;
      filter: MarketplaceDynamicFilter;
    }
  | {
      kind: "chip";
      key: string;
      isSelected: boolean;
      sortIndex: number;
      chip: FilterChipItem;
    };

export type FilterBarSlot = "mobile" | "desktop";

type FilterBarContextValue = {
  openBarPopoverKey: string | null;
  setBarPopoverOpen: (key: string, isOpen: boolean) => void;
  closeBarPopover: () => void;
  dateSheetOpen: boolean;
  setDateSheetOpen: (open: boolean) => void;
  dateFilter: MarketplaceDynamicFilter | undefined;
  sortedBarTags: BarTagItem[];
  selectedFilters: MarketplaceSelectedFilters;
  onFilterChange: (
    type: string,
    value: MarketplaceFilterValue | undefined
  ) => void;
  onOpenFiltersModal: () => void;
  activeModalFilterCount: number;
  isFetchingFilters: boolean;
  handleChipToggle: (
    type: string,
    optionId: number,
    dismissPopover?: boolean
  ) => void;
};

const FilterBarContext = createContext<FilterBarContextValue | null>(null);

function useFilterBarContext() {
  const ctx = useContext(FilterBarContext);
  if (!ctx) {
    throw new Error(
      "Filter bar components must be used within FilterBarProvider"
    );
  }
  return ctx;
}

function useTagScrollHints(
  scrollRef: RefObject<HTMLDivElement | null>,
  contentKey: string
) {
  const [showLeftHint, setShowLeftHint] = useState(false);
  const [showRightHint, setShowRightHint] = useState(false);

  const updateScrollHints = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

    setShowLeftHint(hasOverflow && !atStart);
    setShowRightHint(hasOverflow && !atEnd);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollHints();
    el.addEventListener("scroll", updateScrollHints, { passive: true });

    const observer = new ResizeObserver(updateScrollHints);
    observer.observe(el);
    for (const child of el.children) {
      observer.observe(child);
    }

    window.addEventListener("resize", updateScrollHints);

    const frame = requestAnimationFrame(updateScrollHints);
    const delayed = window.setTimeout(updateScrollHints, 150);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(delayed);
      el.removeEventListener("scroll", updateScrollHints);
      observer.disconnect();
      window.removeEventListener("resize", updateScrollHints);
    };
  }, [scrollRef, updateScrollHints, contentKey]);

  const scrollBy = useCallback(
    (direction: "left" | "right") => {
      scrollRef.current?.scrollBy({
        left: direction === "left" ? -160 : 160,
        behavior: "smooth",
      });
    },
    [scrollRef]
  );

  return { showLeftHint, showRightHint, scrollBy };
}

function useFilterBarState({
  filters,
  selectedFilters,
  onFilterChange,
  onOpenFiltersModal,
  activeModalFilterCount,
  isFetchingFilters = false,
}: FilterBarProps): FilterBarContextValue {
  const [openBarPopoverKey, setOpenBarPopoverKey] = useState<string | null>(
    null
  );
  const [dateSheetOpen, setDateSheetOpen] = useState(false);

  const closeBarPopover = () => setOpenBarPopoverKey(null);

  const setBarPopoverOpen = (key: string, isOpen: boolean) => {
    setOpenBarPopoverKey(isOpen ? key : null);
  };

  const barFilters = filters.filter((f) => f.placement === "bar");
  const dateFilter = barFilters.find((f) => f.input_type === "date_range");
  const checkboxBarFilters = barFilters.filter(
    (f) => f.input_type === "checkbox"
  );
  const chipFilters = checkboxBarFilters.filter(
    (f) => f.bar_display !== "dropdown"
  );
  const dropdownFilters = checkboxBarFilters.filter(
    (f) => f.bar_display === "dropdown"
  );

  const handleChipToggle = (
    type: string,
    optionId: number,
    dismissPopover = true
  ) => {
    if (dismissPopover) closeBarPopover();
    const current = getCheckboxSelection(selectedFilters, type);
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    onFilterChange(type, next.length > 0 ? next : undefined);
  };

  const sortedBarTags: BarTagItem[] = (() => {
    const items: BarTagItem[] = [];
    let sortIndex = 0;

    dropdownFilters.forEach((filter) => {
      items.push({
        kind: "dropdown",
        key: filter.type,
        isSelected:
          getCheckboxSelection(selectedFilters, filter.type).length > 0,
        sortIndex: sortIndex++,
        filter,
      });
    });

    chipFilters.forEach((filter) => {
      const options = filter.options ?? [];
      const selectedIds = getCheckboxSelection(selectedFilters, filter.type);
      const orderedOptions = [
        ...options.filter((option) => selectedIds.includes(option.id)),
        ...options.filter((option) => !selectedIds.includes(option.id)),
      ];
      const visibleOptions =
        filter.type === MARKETPLACE_KEYWORDS_FILTER_TYPE
          ? orderedOptions.slice(0, MAX_KEYWORD_CHIPS)
          : orderedOptions;

      visibleOptions.forEach((option) => {
        const isSelected = selectedIds.includes(option.id);
        const key = `${filter.type}-${option.id}`;
        const index = sortIndex++;

        items.push({
          kind: "chip",
          key,
          isSelected,
          sortIndex: index,
          chip: {
            key,
            filterType: filter.type,
            optionId: option.id,
            label: option.label,
            isSelected,
            sortIndex: index,
          },
        });
      });
    });

    return items.sort((a, b) => a.sortIndex - b.sortIndex);
  })();

  return {
    openBarPopoverKey,
    setBarPopoverOpen,
    closeBarPopover,
    dateSheetOpen,
    setDateSheetOpen,
    dateFilter,
    sortedBarTags,
    selectedFilters,
    onFilterChange,
    onOpenFiltersModal,
    activeModalFilterCount,
    isFetchingFilters,
    handleChipToggle,
  };
}

/** Single mobile date sheet — avoids duplicate sheets from dual filter bars. */
function FilterBarMobileDateSheet() {
  const {
    dateSheetOpen,
    setDateSheetOpen,
    dateFilter,
    selectedFilters,
    onFilterChange,
  } = useFilterBarContext();

  if (!dateFilter) return null;

  const dateValue = selectedFilters[dateFilter.type] as
    | MarketplaceDateFilterValue
    | undefined;

  return (
    <Sheet open={dateSheetOpen} onOpenChange={setDateSheetOpen}>
      <SheetContent
        side="bottom"
        className="flex max-h-[92dvh] flex-col overflow-hidden rounded-t-2xl bg-white p-0 md:hidden [&>button]:hidden">
        <SheetTitle className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-2.5">
          <span className="text-lg font-semibold text-gray-900">
            {dateFilter.label}
          </span>
          <SheetClose className="flex items-center justify-center rounded-full border border-gray-300 p-1.5 text-gray-900">
            <X className="h-4 w-4" />
          </SheetClose>
        </SheetTitle>
        <MarketplaceMobileDatePickerPanel
          className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-3 pb-3"
          label={dateFilter.label}
          value={dateValue}
          onChange={(val) => onFilterChange(dateFilter.type, val)}
          isOpen={dateSheetOpen}
          onApply={() => setDateSheetOpen(false)}
        />
      </SheetContent>
    </Sheet>
  );
}

export function FilterBarProvider({
  children,
  ...props
}: FilterBarProps & { children: ReactNode }) {
  const value = useFilterBarState(props);
  return (
    <FilterBarContext.Provider value={value}>
      {children}
      <FilterBarMobileDateSheet />
    </FilterBarContext.Provider>
  );
}

const filterTagClearIconClass = "h-3.5 w-3.5 shrink-0 opacity-80";

/** Filters + date at the start of the tag strip (scrolls with chips) */
function FilterBarScrollLeadingControls({
  slot,
  scrollContainerRef,
}: {
  slot: FilterBarSlot;
  scrollContainerRef?: RefObject<HTMLElement | null>;
}) {
  const {
    closeBarPopover,
    onOpenFiltersModal,
    activeModalFilterCount,
    dateFilter,
    selectedFilters,
    onFilterChange,
    openBarPopoverKey,
    setBarPopoverOpen,
    setDateSheetOpen,
  } = useFilterBarContext();

  const isMobile = useIsMobileViewport();
  const showMobileDateTrigger = slot === "mobile" && isMobile;
  const showDesktopDateFilter = slot === "desktop" && !isMobile;

  const dateValue = dateFilter
    ? (selectedFilters[dateFilter.type] as
        | MarketplaceDateFilterValue
        | undefined)
    : undefined;
  const hasDates = hasCompleteDateRange(dateValue);
  const dateRangeLabel = formatMarketplaceDateRange(dateValue);
  const isDateActive = Boolean(dateValue?.start);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          closeBarPopover();
          onOpenFiltersModal();
        }}
        className={cn(
          "inline-flex w-max shrink-0 items-center gap-1.5 whitespace-nowrap",
          activeModalFilterCount > 0
            ? filterChipActiveClass
            : filterChipInactiveClass
        )}>
        <ListFilter className="h-4 w-4 shrink-0" />
        <span>Filters</span>
        {activeModalFilterCount > 0 && (
          <span className="bg-brand-500 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-bold text-white">
            {activeModalFilterCount}
          </span>
        )}
      </button>

      {dateFilter && showMobileDateTrigger && (
        <button
          type="button"
          onClick={() => {
            closeBarPopover();
            setDateSheetOpen(true);
          }}
          className={cn(
            "inline-flex w-max shrink-0 items-center gap-1.5 whitespace-nowrap",
            isDateActive || hasDates
              ? filterChipActiveClass
              : filterChipInactiveClass
          )}>
          <Calendar className="h-4 w-4 shrink-0" />
          <span>
            {hasDates && dateRangeLabel ? dateRangeLabel : dateFilter.label}
          </span>
          {hasDates ? (
            <span
              role="button"
              tabIndex={0}
              className="inline-flex shrink-0"
              aria-label={`Clear ${dateFilter.label}`}
              onClick={(e) => {
                e.stopPropagation();
                onFilterChange(dateFilter.type, undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onFilterChange(dateFilter.type, undefined);
                }
              }}>
              <X className={filterTagClearIconClass} strokeWidth={2.5} />
            </span>
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
          )}
        </button>
      )}

      {dateFilter && showDesktopDateFilter && (
        <div className="w-max shrink-0">
          <DateRangeFilter
            label={dateFilter.label}
            value={dateValue}
            isActive={isDateActive}
            open={openBarPopoverKey === dateFilter.type}
            onOpenChange={(isOpen) =>
              setBarPopoverOpen(dateFilter.type, isOpen)
            }
            onChange={(val) => onFilterChange(dateFilter.type, val)}
            scrollContainerRef={scrollContainerRef}
          />
        </div>
      )}
    </>
  );
}

/** Horizontally scrollable filter tags */
export function FilterBarTags({ slot }: { slot: FilterBarSlot }) {
  const {
    sortedBarTags,
    selectedFilters,
    activeModalFilterCount,
    isFetchingFilters,
    dateFilter,
    openBarPopoverKey,
    setBarPopoverOpen,
    onFilterChange,
    handleChipToggle,
  } = useFilterBarContext();

  const tagsScrollRef = useRef<HTMLDivElement>(null);
  const dateFilterKey = dateFilter
    ? JSON.stringify(selectedFilters[dateFilter.type] ?? null)
    : "";
  const tagsContentKey = `${sortedBarTags.map((t) => t.key).join("|")}|${activeModalFilterCount}|${dateFilterKey}`;
  const { showLeftHint, showRightHint, scrollBy } = useTagScrollHints(
    tagsScrollRef,
    tagsContentKey
  );

  return (
    <div
      className={cn(
        "border-gray-blue-100 border-b bg-white",
        slot === "mobile" && "border-t-0"
      )}>
      <div
        className={cn(
          "app-container md:pt-4 md:pb-5",
          slot === "mobile" ? "pt-2 pb-2" : "max-md:py-2"
        )}>
        <div className="relative min-w-0">
          <div
            ref={tagsScrollRef}
            className={cn(
              "flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] md:gap-3 [&::-webkit-scrollbar]:hidden",
              showLeftHint && "scroll-pl-10",
              showRightHint && "scroll-pr-10"
            )}>
            <FilterBarScrollLeadingControls
              slot={slot}
              scrollContainerRef={tagsScrollRef}
            />
            {isFetchingFilters ? (
              <FilterBarTagsSkeleton />
            ) : (
              sortedBarTags.map((item) => {
              if (item.kind === "dropdown") {
                return (
                  <div key={item.key} className="w-max shrink-0">
                    <FilterDropdown
                      label={item.filter.label}
                      options={item.filter.options ?? []}
                      selectedIds={getCheckboxSelection(
                        selectedFilters,
                        item.filter.type
                      )}
                      open={openBarPopoverKey === item.filter.type}
                      onOpenChange={(isOpen) =>
                        setBarPopoverOpen(item.filter.type, isOpen)
                      }
                      scrollContainerRef={tagsScrollRef}
                      onToggle={(optionId) =>
                        handleChipToggle(item.filter.type, optionId, false)
                      }
                      onClear={() =>
                        onFilterChange(item.filter.type, undefined)
                      }
                    />
                  </div>
                );
              }

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() =>
                    handleChipToggle(item.chip.filterType, item.chip.optionId)
                  }
                  className={cn(
                    "gap-1.5",
                    item.chip.isSelected
                      ? filterChipActiveClass
                      : filterChipInactiveClass
                  )}>
                  <span className="whitespace-nowrap">{item.chip.label}</span>
                  {item.chip.isSelected && (
                    <X
                      className={filterTagClearIconClass}
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  )}
                </button>
              );
            })
            )}
          </div>

          {showLeftHint && (
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-11 bg-gradient-to-r from-white from-50% via-white/95 to-transparent">
              <button
                type="button"
                onClick={() => scrollBy("left")}
                className="pointer-events-auto absolute top-1/2 left-0 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-gray-500 transition-colors hover:text-gray-900"
                aria-label="Scroll tags left">
                <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          )}

          {showRightHint && (
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-11 bg-gradient-to-l from-white from-50% via-white/95 to-transparent">
              <button
                type="button"
                onClick={() => scrollBy("right")}
                className="pointer-events-auto absolute top-1/2 right-0 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-gray-500 transition-colors hover:text-gray-900"
                aria-label="Scroll tags right">
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Mobile filter row (tags + filters/date chips) */
export function FilterBar(props: FilterBarProps) {
  return (
    <FilterBarProvider {...props}>
      <FilterBarTags slot="mobile" />
    </FilterBarProvider>
  );
}
